import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import * as XLSX from 'xlsx';
import '../../css/Pages.css';
import UserStandardHourListing from '../../components/Scoring/StandardHour/UserStandardHourListing';
import UserStandardHourForm from '../../components/Scoring/StandardHour/UserStandardHourForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';

const UserStandardHour = () => {
    const toast = useRef(null);
    const today = new Date().toISOString().split('T')[0];

    const initialForm = { StandardHourId: '', ActualPercentage: '', StartDate: today, EndDate: today, IsCurrent: false, Notes: '' };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [editId, setEditId] = useState(null);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        toast.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn hoặc không có quyền truy cập!', life: 3000 });
        setTimeout(() => {
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            window.location.href = '/login';
        }, 1500);
    }, []);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/standardhour`, {
                headers: getAuthHeaders()
            });
            if (res.status === 401 || res.status === 403) return handleLogout();
            if (res.ok) setCategories(await res.json());
        } catch (error) {
            console.error("Lỗi tải danh mục:", error);
        }
    }, [getAuthHeaders, handleLogout]);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                handleLogout();
                if (!isBackground) setIsLoading(false);
                return;
            }
            const currentUser = JSON.parse(userStr);
            const userRoleName = (currentUser.RoleName || '').toLowerCase();
            const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
                ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/userstandardhour?userId=${currentUser.Id}&isAdmin=${isManager}`,
                {
                    method: 'GET',
                    headers: getAuthHeaders()
                }
            );

            if (response.status === 401 || response.status === 403) return handleLogout();

            if (response.ok) {
                const result = await response.json();
                setData(result);
                setFilteredData(result);
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể tải dữ liệu!', life: 3000 });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchData();
        fetchCategories();
    }, [fetchData, fetchCategories]);

    const handleSearch = (e) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(query);
        setFilteredData(data.filter(item => item.StandardHourName && item.StandardHourName.toLowerCase().includes(query)));
    };

    const handleUpdateStatus = async (id, newStatus) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userstandardhour`, {
                method: 'PATCH',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: id.toString(), status: newStatus })
            });

            if (response.status === 401 || response.status === 403) return handleLogout();

            if (response.ok) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? 'Đã gửi yêu cầu duyệt!' : 'Đã hủy yêu cầu duyệt!',
                    life: 3000
                });
                fetchData(true);
            } else {
                const err = await response.json();
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.message || 'Thao tác thất bại!', life: 3000 });
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
        }
    };

    const handleBulkUpdateStatus = async (ids, newStatus) => {
        if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt để có thể thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Đang chỉnh sửa') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã gửi duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã gửi duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/userstandardhour`, {
                    method: 'PATCH',
                    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: id.toString(), status: newStatus })
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current?.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? `Đã gửi yêu cầu duyệt ${ids.length} mục!` : `Đã hủy gửi duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleCurrent = async (item) => {
        if (item.Status === 'Đã gửi duyệt' || item.Status === 'Đã duyệt') {
            toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Bản ghi đã khóa, không thể thay đổi trạng thái áp dụng!', life: 3000 });
            return;
        }

        try {
            const payload = {
                ...item,
                IsCurrent: !item.IsCurrent
            };
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userstandardhour`, {
                method: 'PUT',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json; charset=UTF-8' },
                body: JSON.stringify(payload)
            });

            if (response.status === 401 || response.status === 403) return handleLogout();

            if (response.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã cập nhật trạng thái áp dụng!', life: 3000 });
                fetchData(true);
            } else {
                const err = await response.json();
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.message || 'Cập nhật thất bại!', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const userStr = localStorage.getItem('user');
        if (!userStr) return handleLogout();
        const currentUser = JSON.parse(userStr);

        if (editId) {
            const currentItem = data.find(d => d.Id === editId);
            if (currentItem?.Status === 'Đã gửi duyệt' || currentItem?.Status === 'Đã duyệt') {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Bản ghi đã khóa, không thể lưu!', life: 3000 });
            }
        }

        const method = editId ? 'PUT' : 'POST';

        const payload = {
            ...formData,
            UserId: parseInt(currentUser.Id),
            StandardHourId: parseInt(formData.StandardHourId),
            ActualPercentage: parseFloat(formData.ActualPercentage)
        };
        if (editId) payload.Id = editId;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userstandardhour`, {
                method,
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json; charset=UTF-8' },
                body: JSON.stringify(payload)
            });

            if (response.status === 401 || response.status === 403) return handleLogout();

            let result;
            try { result = await response.json(); } catch (err) { result = {}; }

            if (response.ok && result.status !== 'error') {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: editId ? 'Cập nhật thành công!' : 'Thêm mới thành công!', life: 3000 });
                fetchData(true);
                closeModal();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: result.message || 'Lưu dữ liệu thất bại!', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleEdit = (item) => {
        if (item.Status === 'Đã gửi duyệt' || item.Status === 'Đã duyệt') {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Bản ghi đã khóa, không thể chỉnh sửa!', life: 3000 });
        }
        setEditId(item.Id);
        setFormData({ ...item });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        const item = data.find(d => d.Id === id);
        if (item?.Status === 'Đã gửi duyệt' || item?.Status === 'Đã duyệt') {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Bản ghi đã khóa, không thể xóa!', life: 3000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa mục giờ định mức này?',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userstandardhour?id=${id}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders()
                    });

                    if (response.status === 401 || response.status === 403) return handleLogout();

                    if (response.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa dữ liệu!', life: 3000 });
                        fetchData(true);
                    } else {
                        const err = await response.json();
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.message || 'Xóa thất bại!', life: 3000 });
                    }
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
                }
            }
        });
    };

    const handleBulkDelete = (ids) => {
        const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
        if (invalidItems.length > 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các bản ghi đã gửi hoặc đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} mục giờ định mức đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/userstandardhour?id=${id}`, {
                            method: 'DELETE',
                            headers: getAuthHeaders()
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const closeModal = () => { setIsModalOpen(false); setFormData(initialForm); setEditId(null); };

    const exportToExcel = () => {
        const dataToExport = filteredData;

        if (dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const excelData = dataToExport.map((item, index) => ({
            'STT': index + 1,
            'Nội dung định mức': item.StandardHourName || '',
            'Giờ thực tế': item.ActualPercentage || 0,
            'Từ ngày': item.StartDate ? new Date(item.StartDate).toLocaleDateString('vi-VN') : '',
            'Đến ngày': item.EndDate ? new Date(item.EndDate).toLocaleDateString('vi-VN') : '',
            'Trạng thái áp dụng': item.IsCurrent ? 'Đang áp dụng' : 'Hết hạn',
            'Trạng thái duyệt': item.Status || 'Đang chỉnh sửa',
            'Ghi chú': item.Notes || ''
        }));

        excelData.push({
            'STT': '',
            'Nội dung định mức': 'TỔNG CỘNG:',
            'Giờ thực tế': `${dataToExport.length} Bản ghi`,
            'Từ ngày': '',
            'Đến ngày': '',
            'Trạng thái áp dụng': '',
            'Trạng thái duyệt': '',
            'Ghi chú': ''
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "GioDinhMuc");
        XLSX.writeFile(workbook, `DanhSachGioDinhMuc_${today}.xlsx`);
    };

    const mergedData = filteredData.map(item => {
        const matchedCategory = categories.find(c => c.Id === item.StandardHourId);
        return {
            ...item,
            Score: matchedCategory ? matchedCategory.Score : null
        };
    });

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" />
            <div className="page-header">
                <div className="header-title">
                    <h2>QUẢN LÝ GIỜ ĐỊNH MỨC CÁ NHÂN</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                <button className="btn-add-new" onClick={() => setIsModalOpen(true)} style={{ margin: 0 }}>
                    <i className="fa-solid fa-plus"></i> Thêm mới
                </button>
                <button
                    className="btn-add-new"
                    style={{ backgroundColor: '#1a9953', margin: 0 }}
                    onClick={exportToExcel}
                >
                    <i className="fa-solid fa-file-excel"></i> Xuất Excel
                </button>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '5px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                    <p className="sub-title" style={{ margin: 0 }}>DANH SÁCH GIỜ ĐỊNH MỨC</p>
                    <div className="search-wrapper" style={{ position: 'relative', width: '100%', maxWidth: '350px' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                        <input type="text" placeholder="Tìm kiếm" className="form-input" style={{ width: '100%', paddingLeft: '35px' }} value={searchQuery} onChange={handleSearch} />
                    </div>
                </div>
            </div>

            <UserStandardHourListing
                data={mergedData}
                searchQuery={searchQuery}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSubmitStatus={(id) => handleUpdateStatus(id, 'Đã gửi duyệt')}
                onCancelSubmit={(id) => handleUpdateStatus(id, 'Đang chỉnh sửa')}
                onBulkSend={(ids) => handleBulkUpdateStatus(ids, 'Đã gửi duyệt')}
                onBulkCancel={(ids) => handleBulkUpdateStatus(ids, 'Đang chỉnh sửa')}
                onBulkDelete={handleBulkDelete}
                onToggleCurrent={handleToggleCurrent}
                isLoading={isLoading}
            />

            <UserStandardHourForm
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                isEditing={!!editId}
                categories={categories}
            />
        </div>
    );
};

export default UserStandardHour;